/**
 * Self-service profile page — every authenticated user can edit their
 * own name, avatar image, and password from one screen.
 *
 * Avatar uploads are kept simple: an image file is read in the browser
 * via the FileReader API and stored as a ``data:image/...`` base64 URI
 * on the user document.  Hard-capped at 300 KB to keep the user doc
 * lean and the wire payload small.  When we later add S3 / R2 storage,
 * the server side can transparently swap the inline data URI for a
 * permanent CDN URL — the field stays ``avatar_url`` either way.
 *
 * The page is universal — same surface for every role.  It deliberately
 * does NOT expose the user's role / org / RBAC; those are managed by
 * an org_admin elsewhere.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, KeyRound, User as UserIcon } from "lucide-react";

import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/DashboardLayout";
import CockpitHero from "../components/CockpitHero";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const MAX_AVATAR_BYTES = 300 * 1024; // 300 KB before base64 inflation

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);

  // Profile form
  const [name, setName] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setAvatarPreview(user.avatar_url || "");
    }
  }, [user]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (PNG, JPG, or WebP).");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error(`That image is ${Math.round(file.size / 1024)} KB — please keep it under 300 KB.`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataURL(file);
      setAvatarPreview(dataUrl);
      toast.success("Picture ready — click Save to apply.");
    } catch {
      toast.error("Couldn't read that image. Try a different file.");
    } finally {
      // Reset the input so picking the same file again still fires onChange.
      e.target.value = "";
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    const payload = {};
    if (name && name !== user?.name) payload.name = name;
    if (avatarPreview !== user?.avatar_url) payload.avatar_url = avatarPreview;
    if (Object.keys(payload).length === 0) {
      toast.info("Nothing to save — your profile is up to date.");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/auth/me/profile", payload);
      if (refresh) await refresh();
      toast.success("Profile updated.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatApiError(detail) || err.message || "Could not save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPwd !== newPwd2) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setChangingPwd(true);
    try {
      await api.post("/auth/me/password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password updated. Sign in with your new password next time.");
      setCurrentPwd("");
      setNewPwd("");
      setNewPwd2("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatApiError(detail) || err.message || "Could not change password.");
    } finally {
      setChangingPwd(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-10" data-testid="profile-page">
        <CockpitHero
          eyebrow="Your profile"
          title="Account"
          titleAccent="settings."
          subtitle="Update your display name, profile picture, and password. These are personal — your role and organisation are managed by your organiser."
        />

        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 max-w-6xl">
          {/* ─── Profile (name + avatar) ─── */}
          <section
            className="rounded-2xl bg-white border border-slate-200 p-8 shadow-[0_2px_30px_rgba(15,76,129,0.04)]"
            data-testid="profile-identity-card"
          >
            <div className="flex items-center gap-2 mb-6">
              <UserIcon className="h-4 w-4 text-[#D4AF37]" />
              <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500">
                Identity
              </p>
            </div>

            <form onSubmit={saveProfile} className="space-y-5" data-testid="profile-form">
              {/* Avatar */}
              <div className="flex items-center gap-5">
                <div
                  className="h-20 w-20 rounded-full bg-gradient-to-br from-[#0A192F] to-[#0F4C81] border-4 border-white shadow-[0_4px_16px_rgba(10,25,47,0.18)] overflow-hidden flex items-center justify-center font-display text-3xl text-[#D4AF37]"
                  data-testid="profile-avatar-preview"
                >
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    (user?.name || "?")[0]
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
                    Profile picture
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                      className="border-slate-300 text-[#0F4C81] hover:bg-slate-50"
                      data-testid="profile-upload-button"
                    >
                      <Camera className="h-3.5 w-3.5 mr-2" />
                      Upload
                    </Button>
                    {avatarPreview ? (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setAvatarPreview("")}
                        className="text-slate-500 hover:bg-slate-100"
                        data-testid="profile-clear-avatar"
                      >
                        Remove
                      </Button>
                    ) : null}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={onPickFile}
                      className="hidden"
                      data-testid="profile-file-input"
                    />
                  </div>
                  <p className="text-[11px] text-slate-400">
                    PNG / JPG / WebP, up to 300 KB. Square images render best.
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-slate-700 text-xs uppercase tracking-[0.16em]">
                  Display name
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Riya Kapoor"
                  className="mt-1.5"
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <Label className="text-slate-700 text-xs uppercase tracking-[0.16em]">
                  Email
                </Label>
                <Input
                  value={user?.email || ""}
                  disabled
                  className="mt-1.5 bg-slate-50 text-slate-500"
                  data-testid="profile-email-readonly"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Email changes are not self-service — contact support if you need this changed.
                </p>
              </div>

              <Button
                type="submit"
                disabled={savingProfile}
                className="bg-[#0F4C81] hover:bg-[#0A3860] text-white"
                data-testid="profile-save-button"
              >
                {savingProfile ? "Saving…" : "Save profile"}
              </Button>
            </form>
          </section>

          {/* ─── Password change ─── */}
          <section
            className="rounded-2xl bg-[#0A192F] text-white border border-[#D4AF37]/20 p-8 shadow-[0_4px_30px_rgba(10,25,47,0.12)] relative overflow-hidden"
            data-testid="profile-password-card"
          >
            <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 80% 20%, rgba(212,175,55,0.6) 0, transparent 50%)",
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-2 mb-6">
                <KeyRound className="h-4 w-4 text-[#D4AF37]" />
                <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#D4AF37]">
                  Security
                </p>
              </div>

              <h3 className="font-display text-2xl mb-1">
                Change <span className="italic font-medium text-[#D4AF37]">password.</span>
              </h3>
              <p className="text-white/60 text-xs mb-6">
                Pick something long. We hash it server-side with bcrypt.
              </p>

              <form onSubmit={changePassword} className="space-y-4" data-testid="password-form">
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                    Current password
                  </Label>
                  <Input
                    type="password"
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                    data-testid="password-current-input"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                    New password
                  </Label>
                  <Input
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                    data-testid="password-new-input"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.16em]">
                    Confirm new password
                  </Label>
                  <Input
                    type="password"
                    value={newPwd2}
                    onChange={(e) => setNewPwd2(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:border-[#D4AF37]"
                    data-testid="password-confirm-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={changingPwd}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A192F] font-bold tracking-wide uppercase text-xs"
                  data-testid="password-save-button"
                >
                  {changingPwd ? "Updating…" : "Update password"}
                </Button>
                <p className="text-[11px] text-white/40 pt-2">
                  Tip: 12+ chars with a mix of letters, numbers, and symbols.
                </p>
              </form>
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
}
